import { useLocalStorageState } from 'ahooks';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import { FirebaseError } from 'firebase/app';
import {
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import React, { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clearCachedToken, get, post, renewToken } from 'src/api/requests';
import { Input } from 'src/components/Input';
import { Loading } from 'src/components/Loading';
import { PasswordInput } from 'src/components/PasswordInput';
import { SignupCard } from 'src/components/SignupCard';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { getFirebaseErrorMessage } from 'src/utils/FirebaseErrorUtils';
import { Button } from '../components/Button';
import { AccountDisclaimers } from '../components/AccountDisclaimers';
import InfoPanel from 'src/components/InfoPanel';
import { UsersThree } from '@phosphor-icons/react';

export const SignUp = () => {
  const [searchParams] = useSearchParams();
  const invitedOrgId = searchParams.get('organizationId');
  const { environment, updateEnvironment } = useEnvironment();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const auth = getAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const googleProvider = new GoogleAuthProvider();
  const facebookProvider = new FacebookAuthProvider();
  const appleProvider = new OAuthProvider('apple.com');

  const [, setFireBaseDisplayName] = useLocalStorageState<string | null>(
    '@control-plane/fireBaseDisplayName',
    {
      defaultValue: '',
      listenStorageChange: true,
    },
  );

  const notification = useNotification();
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const resolveRecaptchaToken = async (action: string) => {
    if (!executeRecaptcha) {
      throw new Error('recaptcha_unavailable');
    }

    const token = await executeRecaptcha(action);

    if (!token) {
      throw new Error('recaptcha_failed');
    }

    return token;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      track.errorExperienced({
        error: 'password_mismatch',
        message: 'Passwords do not match',
      });
      notification.error('Passwords do not match!');
      return;
    }

    setLoading(true);

    try {
      const captchaToken = await resolveRecaptchaToken('signup');

      await post(
        '/auth/signup',
        {
          email,
          password,
          name,
        },
        {
          headers: {
            'x-recaptcha-token': captchaToken,
            'x-recaptcha-action': 'signup',
          },
          withoutToken: true,
        },
      );

      track.userCreated({
        signupMethod: 'email',
        email,
      });

      track.formSubmitted({
        formName: 'Sign Up',
      });

      notification.success(
        'User registered successfully! Please confirm your account via the email we just sent you.',
      );

      navigate('/login');
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        track.errorExperienced({
          error: 'firebase_error',
          message: getFirebaseErrorMessage(error.code),
        });
        notification.error(getFirebaseErrorMessage(error.code));
      } else {
        console.error('Signup error:', error);
        track.errorExperienced({
          error: 'unknown_error',
          message: 'Error during signup',
        });
        notification.error(
          'Something went wrong. Please try again in a moment.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignUp = async (
    provider: GoogleAuthProvider | FacebookAuthProvider | OAuthProvider,
    providerName: string,
  ) => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      setFireBaseDisplayName(user.displayName);

      if (user) {
        track.userCreated({
          signupMethod: providerName.toLowerCase(),
          email: user.email || undefined,
        });

        const [fname, lname] = user?.displayName?.split(' ') || [];
        const userEmail = user.email || '';

        try {
          await post(`/users`, {
            fname,
            lname,
            email: userEmail,
          });
          await renewToken();
        } catch (error) {
          if (error instanceof Error) {
            if (error?.message !== 'User found in the system') {
              notification.error(error.message);
              return;
            }
          }
          const err = error as AxiosError;
          if (err.status === 403) {
            notification.error(JSON.parse(err.request.response).message);
            return;
          }
        }

        const environments = await get('/environments');
        const isStoredEnvInEnvironments = environments.find(
          (env: { id: string }) => env.id === environment?.envId,
        );

        const initialEnvId =
          environment?.envId &&
          environment.userId === user.uid &&
          isStoredEnvInEnvironments
            ? environment.envId
            : environments[0].id;

        updateEnvironment({
          userId: user.uid,
          envId: initialEnvId,
        });

        await post(`/users/sync`, {}, { envId: initialEnvId });

        clearCachedToken();

        notification.success(`Sign up with ${providerName} successful!`);

        // sleep 2 seconds to make sure the environment is updated
        await new Promise((resolve) => setTimeout(resolve, 2000));

        navigate('/login');
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        track.errorExperienced({
          error: 'firebase_error',
          message: getFirebaseErrorMessage(error.code),
        });
        notification.error(getFirebaseErrorMessage(error.code));
      } else {
        track.errorExperienced({
          error: 'unknown_error',
          message: 'Error during social signup',
        });
        notification.error(`Error during ${providerName} sign up`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = () => handleSocialSignUp(googleProvider, 'Google');
  const handleFacebookSignUp = () =>
    handleSocialSignUp(facebookProvider, 'Facebook');
  const handleAppleSignUp = () => handleSocialSignUp(appleProvider, 'Apple');

  return (
    <SignupCard>
      {invitedOrgId && (
        <InfoPanel
          title={'You’ve been invited to join a team on Fluents.ai'}
          description={'Create an account or sign in to accept your invite.'}
          icon={<UsersThree size={24} />}
          className="mb-4"
        />
      )}
      <h2 className="mb-4 text-neutral-0">Create your account</h2>

      <form onSubmit={handleSubmit}>
        <div className="mb-2 xl:mb-4">
          <Input
            label="Full Name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={loading}
            className="bg-neutral-0 border-0 text-neutral-600"
            labelClassName="!text-neutral-0 required-field-white"
          />
        </div>

        <div className="mb-2 xl:mb-4">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            className="bg-neutral-0 border-0 text-neutral-600"
            labelClassName="!text-neutral-0 required-field-white"
          />
        </div>

        <div className="mb-2 xl:mb-4">
          <PasswordInput
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            className="bg-neutral-0 border-0 text-neutral-600"
            labelClassName="!text-neutral-0 required-field-white"
          />
        </div>

        <div className="mb-4 xl:mb-4">
          <PasswordInput
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            className="bg-neutral-0 border-0 text-neutral-600"
            labelClassName="!text-neutral-0 required-field-white"
          />
        </div>

        <Button className="w-full mb-6" type="submit" disabled={loading}>
          {loading ? 'Loading...' : 'Sign Up'}
        </Button>

        <div className="relative mb-6">
          <div className="flex items-center justify-center">
            <div className="w-full border-t border-neutral-200"></div>
            <span className="absolute bg-neutral-600 px-2 text-neutral-0">
              or
            </span>
          </div>
        </div>

        <div className="w-full flex flex-col justify-center">
          <button
            onClick={handleGoogleSignUp}
            disabled={loading}
            className={clsx(
              'w-full mb-4 h-10 relative flex items-center justify-center rounded border border-neutral-200 bg-neutral-0 hover:bg-neutral-50',
              loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            )}
          >
            <div className="absolute left-4">
              <img src="/assets/google.svg" width={22} height={22} />
            </div>
            {loading ? (
              <div className="flex gap-2 items-center justify-center">
                <Loading size={'4'} />
                <p>Loading...</p>
              </div>
            ) : (
              <span className="text-sm font-medium text-neutral-600">
                Sign up with Google
              </span>
            )}
          </button>

          {process.env.REACT_APP_ENABLE_SOCIAL_FACEBOOK === 'true' && (
            <button
              onClick={handleFacebookSignUp}
              disabled={loading}
              className={clsx(
                'w-full mb-4 h-10 relative flex items-center justify-center rounded border border-neutral-200 bg-neutral-0 hover:bg-neutral-50',
                loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <div className="absolute left-4">
                <img src="/assets/facebook.svg" width={22} height={22} />
              </div>
              {loading ? (
                <div className="flex gap-2 items-center justify-center">
                  <Loading size={'4'} />
                  <p>Loading...</p>
                </div>
              ) : (
                <span className="text-sm font-medium text-neutral-600">
                  Sign up with Facebook
                </span>
              )}
            </button>
          )}

          {process.env.REACT_APP_ENABLE_SOCIAL_APPLE === 'true' && (
            <button
              onClick={handleAppleSignUp}
              disabled={loading}
              className={clsx(
                'w-full mb-6 h-10 relative flex items-center justify-center rounded border border-neutral-200 bg-neutral-0 hover:bg-neutral-50',
                loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <div className="absolute left-4">
                <img src="/assets/apple.svg" width={22} height={22} />
              </div>
              {loading ? (
                <div className="flex gap-2 items-center justify-center">
                  <Loading size={'4'} />
                  <p>Loading...</p>
                </div>
              ) : (
                <span className="text-sm font-medium text-neutral-600">
                  Sign up with Apple
                </span>
              )}
            </button>
          )}

          <div
            className={clsx(
              'w-full text-center md:text-left font-medium text-body mb-4',
              loading
                ? 'opacity-50 text-neutral-300'
                : 'opacity-100 text-primary-100',
            )}
          >
            Already have an account?{' '}
            <span className="cursor-pointer" onClick={() => navigate('/login')}>
              Log In
            </span>
          </div>

          <AccountDisclaimers
            className="text-neutral-0"
            alignment="responsiveLeft"
          />
        </div>
      </form>
    </SignupCard>
  );
};
